// ============================================================
// PhoneView — iPhone WebView companion (Leaflet + OSM)
// ============================================================
// G2 のモノクロ表示とは別に、ユーザーの手元 (iPhone WebView) で
// 通常の街路地図 (OpenStreetMap) 上に周辺の乗り物・駅・バス停を表示し、
// 駅 / バス停 / バス / 電車を tap → 徒歩ルートを polyline で重ねる。
//
// 構成:
//   - Leaflet 本体 (npm)
//   - OSM タイルは CloudFront の /osm-tiles/* 経由 proxy
//   - ルートは OSRM (driving プロファイル) を /osrm/* 経由で取得

import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import type { NearbyState, Entity } from '../types'
import { distanceMeters } from '../util/distance'
import { OSM_TILES_PREFIX, MAP_RADIUS_MULTIPLIER } from '../config'
import { fetchRoute, type Route } from '../data/routing'

interface SelectedRoute {
  target: Entity
  route: Route | null
  error?: string
}

interface MarkerEntry {
  marker: L.CircleMarker
  entity: Entity
}

export class PhoneView {
  private map: L.Map | null = null
  private tileLayer: L.TileLayer | null = null
  private selfMarker: L.CircleMarker | null = null
  private entityMarkers = new Map<string, MarkerEntry>()
  private routePolyline: L.Polyline | null = null
  private targetHaloMarker: L.CircleMarker | null = null
  private listEl: HTMLElement | null = null
  private clockTimer: number | null = null
  private lastState: NearbyState | null = null
  private selectedRoute: SelectedRoute | null = null
  private routeCache = new Map<string, Route>()
  private autoFollowSelf = true   // 中心追従 (true = state.fix の変化に追従、ユーザー pan で false)

  init(): void {
    this.listEl = document.getElementById('nearby-list')

    const mapEl = document.getElementById('map')
    if (!mapEl) return
    // 初期中心は東京駅 (実 GPS / mock で update 時に置き換わる)
    this.map = L.map(mapEl, {
      center: [35.6812, 139.7671],
      zoom: 15,
      zoomControl: true,
      attributionControl: true,
    })
    this.tileLayer = L.tileLayer(`${OSM_TILES_PREFIX}/{z}/{x}/{y}.png`, {
      maxZoom: 19,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, ' +
        '© <a href="https://carto.com/attributions">CARTO</a>',
    })
    this.tileLayer.addTo(this.map)

    // ユーザーがドラッグ / ズームしたら自動追従を解除
    this.map.on('dragstart', () => { this.autoFollowSelf = false })
    this.map.on('zoomstart', () => { this.autoFollowSelf = false })

    // 地図の空白タップ → ルート解除
    this.map.on('click', (ev) => this.onMapClick(ev))

    // 時計
    const clockEl = document.getElementById('clock')
    const updateClock = () => {
      if (!clockEl) return
      const d = new Date()
      clockEl.textContent =
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
    }
    updateClock()
    this.clockTimer = window.setInterval(updateClock, 1000)
  }

  update(state: NearbyState): void {
    this.lastState = state
    if (!this.map) return
    this.updateSelf(state)
    this.updateEntities(state)
    this.updateList(state)
  }

  // ───────── private: rendering ─────────

  private updateSelf(state: NearbyState): void {
    if (!this.map) return
    const ll = L.latLng(state.fix.lat, state.fix.lon)
    if (!this.selfMarker) {
      this.selfMarker = L.circleMarker(ll, {
        radius: 8,
        weight: 2,
        color: '#ffffff',
        fillColor: '#ff6b6b',
        fillOpacity: 1,
      }).addTo(this.map)
      // 初回は強制 fit
      const radius = state.radiusM * MAP_RADIUS_MULTIPLIER
      const z = zoomForRadius(radius)
      this.map.setView(ll, z)
    } else {
      this.selfMarker.setLatLng(ll)
      if (this.autoFollowSelf) this.map.panTo(ll, { animate: false })
    }
  }

  private updateEntities(state: NearbyState): void {
    if (!this.map) return
    const seen = new Set<string>()
    for (const e of state.nearby) {
      seen.add(e.id)
      const ll = L.latLng(e.lat, e.lon)
      const existing = this.entityMarkers.get(e.id)
      const style = styleForEntity(e)
      if (existing) {
        existing.marker.setLatLng(ll)
        existing.marker.setStyle(style.options)
        existing.entity = e
      } else {
        const m = L.circleMarker(ll, style.options).addTo(this.map)
        m.on('click', (ev) => {
          ev.originalEvent.stopPropagation()  // 地図 click → ルート clear をブロック
          this.onEntityClick(e)
        })
        this.entityMarkers.set(e.id, { marker: m, entity: e })
      }
    }
    // 古い entity を削除
    for (const [id, entry] of this.entityMarkers) {
      if (!seen.has(id)) {
        entry.marker.remove()
        this.entityMarkers.delete(id)
      }
    }
  }

  private drawRoute(route: Route | null, target?: Entity): void {
    if (!this.map) return
    // 既存の route polyline と halo を消す
    this.routePolyline?.remove()
    this.routePolyline = null
    this.targetHaloMarker?.remove()
    this.targetHaloMarker = null

    if (!route || !target) return

    // polyline (GeoJSON は [lon,lat], Leaflet は [lat,lon])
    const latlngs = route.coordinates.map(([lon, lat]) => L.latLng(lat, lon))
    this.routePolyline = L.polyline(latlngs, {
      color: '#ffe066',
      weight: 5,
      opacity: 0.95,
      lineCap: 'round',
      lineJoin: 'round',
    }).addTo(this.map)

    // ターゲットの halo
    this.targetHaloMarker = L.circleMarker(L.latLng(target.lat, target.lon), {
      radius: 14,
      weight: 2,
      color: '#ffe066',
      fillOpacity: 0,
    }).addTo(this.map)

    // fitBounds で全体を入れる (ただし自動追従は解除)
    this.autoFollowSelf = false
    this.map.fitBounds(this.routePolyline.getBounds(), { padding: [30, 30], maxZoom: 17 })
  }

  // ───────── private: click handling ─────────

  private onMapClick(_: L.LeafletMouseEvent): void {
    // 空白タップ → ルートを clear
    if (this.selectedRoute) {
      this.selectedRoute = null
      this.drawRoute(null)
      if (this.lastState) this.updateList(this.lastState)
    }
  }

  private onEntityClick(entity: Entity): void {
    void this.startRouting(entity)
  }

  private async startRouting(target: Entity): Promise<void> {
    const state = this.lastState
    if (!state) return
    this.selectedRoute = { target, route: null }
    this.drawRoute(null, target)
    this.updateList(state)

    const key = routeKey(state.fix.lat, state.fix.lon, target.lat, target.lon)
    try {
      let route = this.routeCache.get(key)
      if (!route) {
        route = await fetchRoute(
          [state.fix.lon, state.fix.lat],
          [target.lon, target.lat],
        )
        this.routeCache.set(key, route)
      }
      if (this.selectedRoute && this.selectedRoute.target === target) {
        this.selectedRoute = { target, route }
        this.drawRoute(route, target)
        if (this.lastState) this.updateList(this.lastState)
      }
    } catch (err) {
      if (this.selectedRoute && this.selectedRoute.target === target) {
        this.selectedRoute = { target, route: null, error: (err as Error).message }
        if (this.lastState) this.updateList(this.lastState)
      }
    }
  }

  // ───────── private: list ─────────

  private updateList(state: NearbyState): void {
    if (!this.listEl) return
    if (this.selectedRoute) {
      const t = this.selectedRoute.target
      const label = t.label ?? entityKindLabel(t)
      let info: string
      if (this.selectedRoute.error) {
        info = `<span style="color:#ff6b6b">ルート取得失敗: ${escapeHtml(this.selectedRoute.error)}</span>`
      } else if (!this.selectedRoute.route) {
        info = '<span style="color:var(--muted)">ルートを取得中…</span>'
      } else {
        const r = this.selectedRoute.route
        info = `<span style="color:var(--accent)">${formatM(r.distanceM)} / 約 ${formatMin(r.durationSec)}分</span>`
      }
      this.listEl.innerHTML = `
        <div style="padding:8px 0;border-bottom:1px solid var(--border)">
          <div style="font-size:11px;color:var(--muted);margin-bottom:4px">目的地</div>
          <div style="font-size:15px;margin-bottom:6px">🏃 ${escapeHtml(label)}</div>
          <div style="font-size:13px">${info}</div>
        </div>
        <button id="clear-route-btn" style="
          margin-top:10px; width:100%; padding:10px;
          background:transparent; color:var(--muted);
          border:1px solid var(--border); border-radius:6px;
          font-size:13px; cursor:pointer;
        ">ルートをクリア</button>
      `
      const btn = document.getElementById('clear-route-btn')
      btn?.addEventListener('click', () => {
        this.selectedRoute = null
        this.drawRoute(null)
        if (this.lastState) this.updateList(this.lastState)
      })
      return
    }

    const movable = state.nearby
      .filter((e) => e.kind === 'bus' || e.kind === 'train')
      .map((e) => ({ e, d: distanceMeters(state.fix, e) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 8)
    if (movable.length === 0) {
      this.listEl.innerHTML = `
        <div class="empty">範囲内に動いている乗り物はありません</div>
        <div class="empty" style="font-size:11px;margin-top:8px;">マップ上の駅 / バス停をタップで徒歩ルート</div>
      `
      return
    }
    const html = movable.map(({ e, d }) => {
      const distLabel = d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`
      const isBus = e.kind === 'bus'
      const kindIcon = isBus ? '●' : '▲'
      const kindColor = isBus ? 'var(--bus)' : 'var(--train)'
      const label = (e.label && e.label !== '') ? e.label : (isBus ? 'バス' : '電車')
      const dim = !e.moving ? ' opacity:0.55;' : ''
      return `<div class="row" style="${dim}">
        <span class="kind" style="color:${kindColor}">${kindIcon}</span>
        <span class="label">${escapeHtml(label)}</span>
        <span class="dist">${distLabel}</span>
      </div>`
    }).join('')
    const hint = '<div class="empty" style="font-size:11px;margin-top:8px;">マップ上の駅 / バス停をタップで徒歩ルート</div>'
    this.listEl.innerHTML = html + hint
  }
}

// ───────── helpers ─────────

interface MarkerStyle {
  options: L.CircleMarkerOptions
}

function styleForEntity(e: Entity): MarkerStyle {
  switch (e.kind) {
    case 'station':
      return { options: {
        radius: 7, weight: 2, color: '#fff', fillColor: '#5a9bff', fillOpacity: 0.95,
      } }
    case 'busstop':
      return { options: {
        radius: 4, weight: 1, color: '#888', fillColor: '#666', fillOpacity: 0.9,
      } }
    case 'train':
      return { options: {
        radius: 7, weight: 2, color: '#fff', fillColor: '#f5a623', fillOpacity: 0.95,
      } }
    case 'bus':
      return { options: {
        radius: 6, weight: 2, color: '#fff',
        fillColor: e.moving ? '#3ec3a7' : '#3a8478', fillOpacity: 0.95,
      } }
    default:
      return { options: { radius: 4, color: '#999', fillColor: '#666' } }
  }
}

function zoomForRadius(radiusM: number): number {
  // 1km radius → zoom 15、5km → 13、10km → 12 程度
  if (radiusM <= 600) return 16
  if (radiusM <= 1500) return 15
  if (radiusM <= 3000) return 14
  if (radiusM <= 7000) return 13
  return 12
}

function formatM(m: number): string {
  if (m < 1000) return `${Math.round(m)}m`
  return `${(m / 1000).toFixed(m % 1000 === 0 ? 0 : 1)}km`
}

function formatMin(sec: number): string {
  const m = Math.max(1, Math.round(sec / 60))
  return String(m)
}

function entityKindLabel(e: Entity): string {
  switch (e.kind) {
    case 'station': return '駅'
    case 'busstop': return 'バス停'
    case 'bus':     return 'バス'
    case 'train':   return '電車'
    default:        return ''
  }
}

function routeKey(lat1: number, lon1: number, lat2: number, lon2: number): string {
  const r = (n: number) => n.toFixed(5)
  return `${r(lat1)},${r(lon1)}→${r(lat2)},${r(lon2)}`
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!))
}
